/**
 * CR-9030: tie the Import Monitor's Customer dropdown to the top right view
 * context. Pure so the page and its tests share one rule.
 */
import type { ImportSourceOption } from '../../lib/api/imports';
import type { ViewLevel } from '../../contexts/ViewContext';

type SiteRef = { id: number; group_id?: number | null; group?: number | null };

export interface ImportMonitorView {
  viewLevel: ViewLevel;
  selectedSiteId: number | null;
  selectedCompanyId: number | null;
  selectedGroupId: number | null;
  sites: SiteRef[];
  /** Superuser or Helix operator: falls back to the first entry, never "no data". */
  unscoped?: boolean;
}

/**
 * The entries the dropdown offers. At Group level only that group's sites,
 * matched on group_id ?? group as useDashboardFilters does; a root group (no
 * site directly in it) or the "all groups" sentinel 0 keeps every entry. A
 * group whose sites have no import data offers nothing.
 */
export function scopeImportOptions(options: ImportSourceOption[], view: ImportMonitorView): ImportSourceOption[] {
  if (view.viewLevel !== 'GROUP' || !view.selectedGroupId) return options;
  const groupSites = new Set(
    view.sites.filter(s => (s.group_id ?? s.group) === view.selectedGroupId).map(s => s.id),
  );
  if (groupSites.size === 0) return options;
  return options.filter(o => groupSites.has(o.customer_id));
}

/**
 * The entry to pre select: the top right site when it has one, else the
 * first entry. '' when a site is selected that has no import data, so the
 * page says so instead of showing another customer's figures. Helix
 * operators keep the first entry: their own company never has imports.
 */
export function defaultImportOptionKey(options: ImportSourceOption[], view: ImportMonitorView): string {
  if (options.length === 0) return '';
  if (view.viewLevel === 'GROUP') return options[0].key;
  // CR-6276: selectedSiteId is authoritative at SITE level; at COMPANY level both match.
  const activeSiteId = view.selectedSiteId ?? view.selectedCompanyId;
  if (!activeSiteId) return options[0].key;
  const match = options.find(o => o.customer_id === activeSiteId)?.key;
  return match ?? (view.unscoped ? options[0].key : '');
}
